// SG surface-search radar: a live PPI scope drawn to a canvas that is both
// textured onto the CIC console screen in 3D and blown up in the station UI.
// Heading-up display, rotating sweep, blips that bloom and fade like
// phosphor. Contacts inside SG range get tracked; CIC can designate them
// to the main battery.

import * as THREE from "three";

export const SG_RANGE = 40000;

export class Radar {
  constructor(G) {
    this.G = G;
    this.canvas = document.createElement("canvas");
    this.canvas.width = this.canvas.height = 512;
    this.ctx = this.canvas.getContext("2d");
    this.tex = new THREE.CanvasTexture(this.canvas);
    this.sweep = 0;           // radians, ship-relative
    this.blips = [];          // {rel bearing, range, age, enemy}
    this.selected = 0;
    this.scale = 40000;       // meters full radius
    this._acc = 0;
  }

  contacts() {
    const G = this.G;
    if (!G.damage.radarAlive) return [];
    const p = G.shipWorldPos();
    return G.enemies().filter((e) => !e.sunk).map((e) => {
      const dx = e.pos.x - p.x, dz = e.pos.y - p.z;
      const range = Math.hypot(dx, dz);
      return { enemy: e, range, bearing: Math.atan2(dx, dz) };
    }).filter((c) => c.range < SG_RANGE).sort((a, b) => a.range - b.range);
  }

  // Shore fire missions the spotter has called in: not radar returns — the
  // grid comes over the NGFS net and CIC plots it on the scope by hand.
  fireMissions() {
    const G = this.G;
    const p = G.shipWorldPos();
    return (G.shore || []).filter((t) => t.called && !t.destroyed).map((t) => {
      const dx = t.pos.x - p.x, dz = t.pos.y - p.z;
      return { enemy: t, range: Math.hypot(dx, dz), bearing: Math.atan2(dx, dz), shore: true };
    }).filter((c) => c.range < SG_RANGE);
  }

  // Everything CIC can hand to the main battery, skunks first.
  list() { return [...this.contacts(), ...this.fireMissions()]; }

  cycle() {
    const c = this.list();
    if (!c.length) return null;
    this.selected = (this.selected + 1) % c.length;
    this.G.audio.ding();
    return c[this.selected];
  }
  current() {
    const c = this.list();
    if (!c.length) return null;
    return c[Math.min(this.selected, c.length - 1)];
  }

  update(dt) {
    const G = this.G;
    if (!G.damage.radarAlive) { this.sweep = 0; }
    else this.sweep = (this.sweep + dt * (Math.PI * 2 / 4)) % (Math.PI * 2);
    // register blips when the sweep passes a contact
    const H = G.shipState.heading;
    for (const c of this.contacts()) {
      const rel = ((c.bearing - H) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      const d = Math.abs(((rel - this.sweep) + Math.PI * 3) % (Math.PI * 2) - Math.PI);
      if (d < dt * (Math.PI * 2 / 4) + 0.02) {
        this.blips.push({ rel, range: c.range, age: 0, name: c.enemy.name });
      }
    }
    for (let i = this.blips.length - 1; i >= 0; i--) {
      this.blips[i].age += dt;
      if (this.blips[i].age > 4.2) this.blips.splice(i, 1);
    }
    this._acc += dt;
    if (this._acc > 0.14) { this._acc = 0; this.draw(); }
  }

  draw() {
    const ctx = this.ctx, S = 512, R = S / 2 - 10, cx = S / 2, cy = S / 2;
    ctx.fillStyle = "#03130a";
    ctx.fillRect(0, 0, S, S);
    const alive = this.G.damage.radarAlive;
    ctx.strokeStyle = "#1d4a2e";
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke();
    // range rings every 10 km
    ctx.strokeStyle = "#123322"; ctx.lineWidth = 1;
    for (let r = 10000; r < this.scale; r += 10000) {
      ctx.beginPath(); ctx.arc(cx, cy, R * r / this.scale, 0, Math.PI * 2); ctx.stroke();
    }
    // bearing ticks
    for (let b = 0; b < 360; b += 30) {
      const a = b * Math.PI / 180;
      ctx.beginPath();
      ctx.moveTo(cx + Math.sin(a) * R * 0.94, cy - Math.cos(a) * R * 0.94);
      ctx.lineTo(cx + Math.sin(a) * R, cy - Math.cos(a) * R);
      ctx.stroke();
    }
    if (!alive) {
      ctx.fillStyle = "#3c7a50";
      ctx.font = "26px monospace"; ctx.textAlign = "center";
      ctx.fillText("SG RADAR — NO VIDEO", cx, cy - 8);
      ctx.font = "16px monospace";
      ctx.fillText("antenna casualty", cx, cy + 18);
      this.tex.needsUpdate = true;
      return;
    }
    // landmass paint: march each bearing out to the first terrain return.
    // Heading-up like everything else on the scope.
    const G = this.G;
    if (G.terrain) {
      const p = G.shipWorldPos();
      const H = G.shipState.heading;
      ctx.fillStyle = "rgba(60,150,90,0.30)";
      const STEPB = Math.PI / 90;
      for (let b = 0; b < Math.PI * 2; b += STEPB) {
        const wb = b + H; // world bearing of this ray
        let r0 = -1;
        for (let r = 800; r <= this.scale; r += 700) {
          if (G.terrain.heightAt(p.x + Math.sin(wb) * r, p.z + Math.cos(wb) * r) > 2) { r0 = r; break; }
        }
        if (r0 < 0) continue;
        // land is the annulus wedge from the first return out to the edge
        ctx.beginPath();
        ctx.arc(cx, cy, R, b - Math.PI / 2, b + STEPB * 1.15 - Math.PI / 2);
        ctx.arc(cx, cy, R * Math.min(1, r0 / this.scale), b + STEPB * 1.15 - Math.PI / 2, b - Math.PI / 2, true);
        ctx.closePath();
        ctx.fill();
      }
    }
    // own-ship marker
    ctx.fillStyle = "#57c07a";
    ctx.fillRect(cx - 2, cy - 5, 4, 10);
    // sweep wedge
    const grd = ctx.createConicGradient
      ? (() => { const g = ctx.createConicGradient(this.sweep - Math.PI / 2, cx, cy); g.addColorStop(0, "rgba(90,220,130,0.30)"); g.addColorStop(0.08, "rgba(90,220,130,0.0)"); g.addColorStop(1, "rgba(90,220,130,0)"); return g; })()
      : null;
    if (grd) { ctx.fillStyle = grd; ctx.beginPath(); ctx.moveTo(cx, cy); ctx.arc(cx, cy, R, this.sweep - Math.PI / 2, this.sweep - Math.PI / 2 + 0.5); ctx.closePath(); ctx.fill(); }
    ctx.strokeStyle = "rgba(120,255,160,0.8)";
    ctx.beginPath(); ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.sin(this.sweep) * R, cy - Math.cos(this.sweep) * R);
    ctx.stroke();
    // blips
    for (const b of this.blips) {
      const f = Math.max(0, 1 - b.age / 4.2);
      const rr = R * Math.min(1, b.range / this.scale);
      const x = cx + Math.sin(b.rel) * rr, y = cy - Math.cos(b.rel) * rr;
      ctx.fillStyle = `rgba(140,255,170,${0.85 * f})`;
      ctx.beginPath(); ctx.ellipse(x, y, 5, 3, b.rel, 0, Math.PI * 2); ctx.fill();
    }
    // fire-mission plots: gold diamonds, hand-plotted from the spotter's grid
    const H2 = this.G.shipState.heading;
    const fms = this.fireMissions();
    ctx.strokeStyle = "#ffcf5c"; ctx.lineWidth = 1.5;
    fms.forEach((m, i) => {
      const rel = m.bearing - H2;
      const rr = R * Math.min(1, m.range / this.scale);
      const x = cx + Math.sin(rel) * rr, y = cy - Math.cos(rel) * rr;
      ctx.beginPath();
      ctx.moveTo(x, y - 7); ctx.lineTo(x + 7, y); ctx.lineTo(x, y + 7); ctx.lineTo(x - 7, y);
      ctx.closePath(); ctx.stroke();
      ctx.fillStyle = "#ffcf5c"; ctx.font = "12px monospace"; ctx.textAlign = "center";
      ctx.fillText(`TGT ${String.fromCharCode(65 + i)}`, x, y - 11);
    });
    // selected contact ring
    const cur = this.current();
    if (cur) {
      const rel = cur.bearing - H2;
      const rr = R * Math.min(1, cur.range / this.scale);
      const x = cx + Math.sin(rel) * rr, y = cy - Math.cos(rel) * rr;
      ctx.strokeStyle = "#ffcf5c"; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(x, y, 11, 0, Math.PI * 2); ctx.stroke();
    }
    // heading marker (ship's head = up)
    ctx.fillStyle = "#57c07a"; ctx.font = "13px monospace"; ctx.textAlign = "center";
    ctx.fillText("SHIP'S HEAD", cx, 18);
    this.tex.needsUpdate = true;
  }

  // A glowing screen mesh for the CIC console.
  makeScreenMesh() {
    const mat = new THREE.MeshBasicMaterial({ map: this.tex });
    const geo = new THREE.PlaneGeometry(1.8, 1.8);
    const m = new THREE.Mesh(geo, mat);
    return m;
  }
}
